"use client";

/* ============================================================
   THEME PROVIDER  —  app/theme/ThemeProvider.jsx

   Menyediakan tiga hal lewat React Context, pola yang sama persis
   dengan LanguageProvider di app/i18n/:

     tema        - "terang" atau "gelap"
     setTema     - penggantinya
     gantiTema() - membalik keduanya

   ============================================================
   KENAPA localStorage DIBACA DI useEffect, BUKAN SAAT RENDER

   Sama seperti di LanguageProvider, dan alasannya sama: peladen
   tidak punya localStorage. Membacanya saat render membuat HTML
   peladen berbeda dari hasil browser, dan React melempar hydration
   error - seluruh halaman gagal dimuat, bukan cuma temanya meleset.

   ⚠️ TAPI DI SINI ADA MASALAH TAMBAHAN YANG TIDAK DIALAMI BAHASA.

   Kalau kelas `dark` baru dipasang setelah React menempel, ada
   jeda beberapa puluh milidetik di mana halaman sudah terlihat
   TERANG sebelum berubah gelap. Kedipan putih di layar gelap itu
   menyakitkan, terutama malam hari - yang justru saat mode gelap
   paling dipakai.

   Karena itu ada skrip kecil di app/layout.js yang berjalan di
   dalam <head>, SEBELUM satu piksel pun digambar. Skrip itu yang
   memasang kelas `dark`. Berkas ini hanya menyusul membaca keadaan
   yang sudah dipasangnya.

   JADI KEDUANYA WAJIB ADA. Menghapus skrip di layout.js tidak
   merusak fungsinya, tapi mengembalikan kedipan putih itu.
   ============================================================ */

import { createContext, useContext, useEffect, useState } from "react";

const KUNCI = "rsjmc-theme";
const TemaContext = createContext(null);

export function ThemeProvider({ children }) {
  /* Nilai awal SELALU "terang" di kedua sisi. Skrip di layout.js
     sudah memasang kelas yang benar di <html>; state di sini
     menyusul di useEffect di bawah. */
  const [tema, setTemaState] = useState("terang");

  useEffect(() => {
    /* Membaca dari DOM, BUKAN dari localStorage.

       Ini disengaja. Skrip di layout.js sudah memutuskan tema -
       termasuk kasus pengunjung yang belum pernah memilih, di mana
       ia mengikuti setelan sistem. Membaca localStorage lagi di
       sini akan melewatkan kasus itu dan menyimpulkan "terang"
       padahal halamannya sudah gelap. */
    const gelap = document.documentElement.classList.contains("dark");
    setTemaState(gelap ? "gelap" : "terang");
  }, []);

  const terapkan = (nilai) => {
    const gelap = nilai === "gelap";

    /* Kelas .ganti-tema membuat perpindahan warnanya halus, lalu
       dilepas lagi. Kalau dibiarkan menyala terus, setiap efek
       hover di situs ikut melambat 200ms dan terasa berat. */
    const akar = document.documentElement;
    akar.classList.add("ganti-tema");
    akar.classList.toggle("dark", gelap);

    window.setTimeout(() => akar.classList.remove("ganti-tema"), 250);

    try {
      window.localStorage.setItem(KUNCI, gelap ? "dark" : "light");
    } catch {
      /* Mode penyamaran atau penyimpanan penuh. Temanya tetap
         berubah untuk kunjungan ini, hanya tidak diingat. */
    }

    setTemaState(nilai);
  };

  const setTema = (nilai) => {
    if (nilai === "terang" || nilai === "gelap") terapkan(nilai);
  };

  const gantiTema = () => terapkan(tema === "gelap" ? "terang" : "gelap");

  return (
    <TemaContext.Provider value={{ tema, setTema, gantiTema }}>
      {children}
    </TemaContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(TemaContext);

  /* Pesan yang menyebut penyebabnya. Tanpa penjagaan ini yang
     muncul adalah "Cannot destructure property 'tema' of null" -
     benar secara teknis, tapi tidak memberi tahu bahwa yang kurang
     adalah pembungkus di layout.js. */
  if (!ctx) {
    throw new Error(
      "useTheme() dipakai di luar <ThemeProvider>. " +
        "Pastikan app/layout.js sudah membungkus {children} dengan provider ini."
    );
  }

  return ctx;
}
